import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import StreamingText from './StreamingText';
import LessonVideoPlayer from '../../../../../../components/LessonVideoPlayer';

interface LessonMedia {
  kind: 'img' | 'video' | 'iframe';
  src: string;
  alt?: string;
}
type Segment = { kind: 'text'; paragraphs: string[] } | { kind: 'media'; media: LessonMedia };

// Elements whose end marks a paragraph boundary when flattening HTML to streamable text.
const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'UL',
  'OL',
  'LI',
  'TABLE',
  'TR',
  'BLOCKQUOTE',
  'PRE'
]);

/**
 * Lesson content arrives as plain text (AIMD) or HTML (manual builder / admin
 * editor). Split it into streamable text paragraphs and the media the admin
 * embedded (<img>/<video>/<iframe>), preserved in document order — previously
 * the HTML was flattened to textContent, which silently dropped all media.
 */
export function parseLessonContent(raw: string): Segment[] {
  if (!raw) return [];
  if (!/<[a-z][\s\S]*>/i.test(raw)) {
    const paragraphs = raw
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean);
    return paragraphs.length ? [{ kind: 'text', paragraphs }] : [];
  }

  const doc = new DOMParser().parseFromString(raw, 'text/html');
  const segments: Segment[] = [];
  let paragraphs: string[] = [];
  let buf = '';

  const flushParagraph = () => {
    const text = buf.replace(/\s+/g, ' ').trim();
    buf = '';
    if (text) paragraphs.push(text);
  };
  const flushText = () => {
    flushParagraph();
    if (paragraphs.length) segments.push({ kind: 'text', paragraphs });
    paragraphs = [];
  };

  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      buf += node.textContent || '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName;
    if (tag === 'IMG' || tag === 'VIDEO' || tag === 'IFRAME') {
      const src =
        el.getAttribute('src') || (tag === 'VIDEO' ? el.querySelector('source')?.getAttribute('src') || '' : '');
      if (src) {
        flushText();
        segments.push({
          kind: 'media',
          media: {
            kind: tag.toLowerCase() as LessonMedia['kind'],
            src,
            alt: el.getAttribute('alt') || undefined
          }
        });
      }
      return;
    }
    if (tag === 'BR') {
      flushParagraph();
      return;
    }
    el.childNodes.forEach(visit);
    if (BLOCK_TAGS.has(tag)) flushParagraph();
  };

  doc.body.childNodes.forEach(visit);
  flushText();
  return segments;
}

const mediaFadeSx = {
  animation: 'elliottMediaIn 0.4s ease',
  '@keyframes elliottMediaIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1 } }
} as const;

const MediaBlock: React.FC<{ media: LessonMedia; animate: boolean }> = ({ media, animate }) => {
  const fade = animate ? mediaFadeSx : {};
  if (media.kind === 'img') {
    return (
      <Box
        component="img"
        src={media.src}
        alt={media.alt || ''}
        sx={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', display: 'block', my: '8px', ...fade }}
      />
    );
  }
  if (media.kind === 'video') {
    return <LessonVideoPlayer src={media.src} maxWidth={640} sx={{ my: '8px', ...fade }} />;
  }
  return (
    <Box
      component="iframe"
      src={media.src}
      title="Lesson media"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      sx={{
        width: '100%',
        maxWidth: 640,
        aspectRatio: '16 / 9',
        border: 'none',
        borderRadius: '8px',
        display: 'block',
        my: '8px',
        ...fade
      }}
    />
  );
};

interface StreamingLessonContentProps {
  content: string;
  sx?: SxProps<Theme>;
  enabled?: boolean; // when false, renders everything immediately (no animation)
  onDone?: () => void;
}

// Streams the lesson content segment by segment: text types out like any other
// Elliott message; embedded media fades in between paragraphs, then the stream
// continues. With `enabled` false everything renders instantly (resume mode).
const StreamingLessonContent: React.FC<StreamingLessonContentProps> = ({ content, sx, enabled = true, onDone }) => {
  const segments = useMemo(() => parseLessonContent(content), [content]);
  const [revealed, setRevealed] = useState(() => (enabled ? Math.min(1, segments.length) : segments.length));
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const fireDone = () => {
    if (!doneRef.current) {
      doneRef.current = true;
      onDoneRef.current?.();
    }
  };

  useEffect(() => {
    doneRef.current = false;
    setRevealed(enabled ? Math.min(1, segments.length) : segments.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, enabled]);

  // Advance past the segment just revealed when it needs no streaming: instant
  // mode, empty content, or a media segment (which "lands", then the stream
  // moves on after its fade).
  const currentIdx = revealed - 1;
  useEffect(() => {
    if (!enabled || segments.length === 0) {
      fireDone();
      return;
    }
    const seg = segments[currentIdx];
    if (!seg || seg.kind !== 'media') return;
    const id = window.setTimeout(() => {
      if (currentIdx + 1 < segments.length) setRevealed(currentIdx + 2);
      else fireDone();
    }, 400);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed, segments, enabled]);

  const handleTextDone = (index: number) => {
    if (enabled && index !== currentIdx) return; // stale callback from an already-passed segment
    if (index + 1 < segments.length) {
      setRevealed((r) => Math.max(r, index + 2));
    } else {
      fireDone();
    }
  };

  return (
    <>
      {segments
        .slice(0, revealed)
        .map((seg, i) =>
          seg.kind === 'text' ? (
            <StreamingText
              key={i}
              paragraphs={seg.paragraphs}
              sx={sx}
              enabled={enabled}
              onDone={() => handleTextDone(i)}
            />
          ) : (
            <MediaBlock key={i} media={seg.media} animate={enabled} />
          )
        )}
    </>
  );
};

export default StreamingLessonContent;
